import React, { useContext } from 'react';

/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { Page } from '../Page';
import { getComponent, getStory } from '../../Docs/Docs';
import 'url-search-params-polyfill';
import { CodePreview } from '../CodePreview/CodePreview';
import {
  TagTabHeaders,
  TagTabHeader,
  styles,
  ITabHeaderChangeArgs,
} from '@tag/tag-components-react-v3';

import { Component, Story } from '../../Docs/DocTypes';
import { Accent } from '../Types';
import {
  renderPills,
  renderComponentDescription,
  renderStoryDescription,
  renderHtmlProps,
  renderReactProps,
  renderHtmlEvents,
  renderHtmlMethods,
  H2,
  getComponentName,
  getStoryName,
  getCodeVisible,
  getCodeBackgroundVisible,
  TabName,
  getCodeTab,
  getPath,
  renderComponentNotForMobile,
  getCodeMobileView,
} from './StoryPage.utils';
import { darkModeContext } from '../../darkModeContext';

export const StoryPage: React.FC<RouteComponentProps> = ({ history, location }) => {
  const componentName: string = getComponentName(location);
  const component: Component | null = getComponent(componentName || '');
  const storyName: string = getStoryName(location);
  const story: Story | null = getStory(componentName || '', storyName || '');
  const codeMobileView: boolean = getCodeMobileView(location);
  const codeVisible: boolean = getCodeVisible(location);
  const codeBackgroundVisible: boolean = getCodeBackgroundVisible(location);
  const codeTab: TabName = getCodeTab(location, story);
  const docsDarkMode = useContext(darkModeContext);
  const docsDarkModeValue = docsDarkMode.value || false;

  if (component === null) {
    return <Page title="Component not found"></Page>;
  }
  if (story === null) {
    return <Page title="Story not found"></Page>;
  }

  const handleCodeVisibleChange = (newCodeVisible: boolean) => {
    history.push(
      getPath(
        componentName,
        storyName,
        newCodeVisible,
        codeTab,
        codeBackgroundVisible,
        codeMobileView,
      ),
    );
  };

  const handleCodeBackgroundChange = (newCodeBackgroundVisible: boolean) => {
    history.push(
      getPath(
        componentName,
        storyName,
        codeVisible,
        codeTab,
        newCodeBackgroundVisible,
        codeMobileView,
      ),
    );
  };

  const handleMobileViewChange = (newMobileView: boolean) => {
    history.push(
      getPath(componentName, storyName, codeVisible, codeTab, codeBackgroundVisible, newMobileView),
    );
  };

  const handleCodeTabChange = (e: CustomEvent<ITabHeaderChangeArgs>) => {
    history.push(
      getPath(
        componentName,
        storyName,
        codeVisible,
        e.detail.name,
        codeBackgroundVisible,
        codeMobileView,
      ),
    );
  };

  const keppel: Accent = 'keppel';
  const access: Accent = 'access';
  const shiraz: Accent = 'shiraz';
  const pills = [
    ...(component.mobileFriendly ? [{ text: 'mobile friendly', accent: keppel }] : []),
    ...(component.notForMobile ? [{ text: 'Not for mobile', accent: access }] : []),
    ...(component.preRelease ? [{ text: component.preRelease, accent: access }] : []),
    ...(component.popular ? [{ text: 'popular', accent: shiraz }] : []),
  ];
  const styles = makeStyles();
  return (
    <Page title={'tag-' + componentName || ''}>
      {pills.length > 0 && renderPills(pills)}
      {renderComponentDescription(component.description)}
      {component.notForMobile && renderComponentNotForMobile()}
      <H2>{storyName}</H2>
      {renderStoryDescription(story.description)}

      <TagTabHeaders
        selectedTab={codeTab}
        onTabHeaderChange={handleCodeTabChange}
        colourScheme={docsDarkModeValue ? 'dark' : 'light'}
      >
        <TagTabHeader name="html" invisible={!story.coreComponent || !story.coreComponent.html}>
          HTML / JS
        </TagTabHeader>
        <TagTabHeader
          name="html_v3"
          invisible={!story.coreComponent_v3 || !story.coreComponent_v3.html}
        >
          HTML / JS v3
        </TagTabHeader>
        <TagTabHeader name="react" invisible={!story.reactWrapper || !story.reactWrapper.jsx}>
          React
        </TagTabHeader>

        <TagTabHeader
          name="react_v3"
          invisible={!story.reactWrapper_v3 || !story.reactWrapper_v3.jsx}
        >
          React v3
        </TagTabHeader>
      </TagTabHeaders>

      <CodePreview
        coreComponent={story.coreComponent ? story.coreComponent : {}}
        coreComponent_v3={story.coreComponent_v3 ? story.coreComponent_v3 : {}}
        reactWrapper={story.reactWrapper ? story.reactWrapper : {}}
        reactWrapper_v3={story.reactWrapper_v3 ? story.reactWrapper_v3 : {}}
        events={component.events}
        mobileFriendly={component.mobileFriendly}
        mobileView={codeMobileView}
        onMobileViewChange={handleMobileViewChange}
        codeVisible={codeVisible}
        onCodeVisibleChange={handleCodeVisibleChange}
        codeBackgroundVisible={codeBackgroundVisible}
        onCodeBackgroundVisibleChange={handleCodeBackgroundChange}
        codeTab={codeTab}
      ></CodePreview>

      <H2>Props</H2>
      {['html', 'html_v3'].includes(codeTab.toLowerCase()) && renderHtmlProps(component)}
      {['react', 'react_v3'].includes(codeTab.toLowerCase()) && renderReactProps(component)}
      {['html', 'html_v3'].includes(codeTab.toLowerCase()) && renderHtmlEvents(component)}
      {['html', 'html_v3'].includes(codeTab.toLowerCase()) && renderHtmlMethods(component)}
      {component.childComponents &&
        component.childComponents.map(child => (
          <div key={child.name} css={styles.childDetails}>
            <h1>{child.name}</h1>
            {renderComponentDescription(child.description)}
            <H2>Props</H2>
            {['html', 'html_v3'].includes(codeTab.toLowerCase()) && renderHtmlProps(child)}
            {['react', 'react_v3'].includes(codeTab.toLowerCase()) && renderReactProps(child)}
            {['html', 'html_v3'].includes(codeTab.toLowerCase()) && renderHtmlEvents(child)}
            {['html', 'html_v3'].includes(codeTab.toLowerCase()) && renderHtmlMethods(child)}
          </div>
        ))}
    </Page>
  );
};

const makeStyles = () => {
  return {
    childDetails: css`
      border-top: 1px solid ${styles.colours.border};
      margin-top: 50px;
      padding-top: 30px;
    `,
  };
};

export default withRouter(StoryPage);
